import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  getAssessmentTypeLabel,
  useWebsiteAssessmentSubmissions,
  type WebsiteAssessmentSubmission,
  type WebsiteAssessmentType,
} from '../hooks/useWebsiteAssessmentSubmissions';
import { formatAssessmentAnswers } from '../utils/websiteAssessmentDisplay';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const HelperText = styled.p`
  color: var(--onSurfaceVariant);
  margin: 0.5rem 0 1rem;
  font-size: 0.9rem;
`;

const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  overflow: hidden;
  margin-bottom: 1.5rem;
  padding: 1.25rem;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FieldRow = styled.div`
  display: grid;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--onSurfaceVariant);
`;

const Input = styled.input`
  padding: 0.65rem 0.8rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  font-size: 0.95rem;
  background: var(--surfaceVariant);
  color: var(--onSurface);
  &:focus {
    outline: 2px solid var(--primary);
    border-color: var(--primary);
  }
`;

const Select = styled.select`
  padding: 0.65rem 0.8rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  font-size: 0.95rem;
  background: var(--surfaceVariant);
  color: var(--onSurface);
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    font-size: 0.9rem;
    color: var(--onSurfaceVariant);
  }
  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--onSurfaceVariant);
    background: var(--surfaceVariant);
  }
  @media (max-width: 768px) {
    th,
    td {
      padding: 0.5rem 0.75rem;
      font-size: 0.85rem;
    }
  }
`;

const ClickableRow = styled.tr`
  cursor: pointer;

  &:hover {
    background: var(--surfaceVariant);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 40;
  padding: 1.5rem;
`;

const ModalCard = styled.div`
  background: var(--surface);
  border-radius: 16px;
  width: min(760px, 100%);
  max-height: min(90vh, 900px);
  overflow: auto;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--outline);
  position: sticky;
  top: 0;
  background: var(--surface);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  color: var(--onSurface);
`;

const ModalCloseButton = styled.button`
  border: none;
  background: transparent;
  color: var(--onSurfaceVariant);
  font-size: 1.15rem;
  cursor: pointer;
`;

const ModalBody = styled.div`
  padding: 1rem 1.25rem 1.25rem;
  display: grid;
  gap: 1rem;
`;

const DetailGroup = styled.div`
  display: grid;
  gap: 0.35rem;
`;

const DetailLabel = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const DetailValue = styled.p`
  margin: 0;
  color: var(--onSurface);
  font-size: 0.95rem;
  white-space: pre-wrap;
  word-break: break-word;
`;

const ResultsList = styled.ol`
  margin: 0;
  padding-left: 1.25rem;
  display: grid;
  gap: 0.75rem;
`;

const AnswerList = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const AnswerItem = styled.div`
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--outline);

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const AnswerPrompt = styled.p`
  margin: 0 0 0.25rem;
  color: var(--onSurface);
  font-size: 0.92rem;
  font-weight: 600;
  line-height: 1.45;
`;

const AnswerValue = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.92rem;
  line-height: 1.45;
`;

const AnswerMeta = styled.p`
  margin: 0 0 0.35rem;
  color: var(--onSurfaceVariant);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const formatTopResults = (submission: WebsiteAssessmentSubmission) => {
  const topResults = submission.results?.topResults ?? [];
  if (topResults.length === 0) return '—';
  return topResults.map((result) => result.label).join(', ');
};

const getFormattedAnswers = (submission: WebsiteAssessmentSubmission) =>
  formatAssessmentAnswers(submission.assessment_type, submission.answers ?? {});

export const AssessmentsAdminPage: React.FC = () => {
  const [assessmentType, setAssessmentType] = useState<'all' | WebsiteAssessmentType>('all');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState<WebsiteAssessmentSubmission | null>(
    null,
  );

  const filters = useMemo(
    () => ({
      assessmentType,
      search,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
    }),
    [assessmentType, search, fromDate, toDate],
  );

  const { data, isLoading, error } = useWebsiteAssessmentSubmissions(filters);
  const hasData = (data?.length ?? 0) > 0;

  useEffect(() => {
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedSubmission(null);
      }
    };

    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, []);

  return (
    <Page>
      <HeaderTitle>Assessments</HeaderTitle>
      <HelperText>
        Submissions from the public website KingsMen ManCave Strengths and Spiritual Gifts assessments.
      </HelperText>

      <Card>
        <FilterGrid>
          <FieldRow>
            <Label htmlFor="assessment-type-filter">Assessment</Label>
            <Select
              id="assessment-type-filter"
              value={assessmentType}
              onChange={(event) =>
                setAssessmentType(event.target.value as 'all' | WebsiteAssessmentType)
              }
            >
              <option value="all">All assessments</option>
              <option value="kingsmen_strengths">KingsMen ManCave Strengths</option>
              <option value="spiritual_gifts">Spiritual Gifts</option>
            </Select>
          </FieldRow>
          <FieldRow>
            <Label htmlFor="assessment-search-filter">Search</Label>
            <Input
              id="assessment-search-filter"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Name, email, or phone"
            />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="assessment-from-date">From</Label>
            <Input
              id="assessment-from-date"
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
            />
          </FieldRow>
          <FieldRow>
            <Label htmlFor="assessment-to-date">To</Label>
            <Input
              id="assessment-to-date"
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
            />
          </FieldRow>
        </FilterGrid>
      </Card>

      {isLoading && <HelperText>Loading…</HelperText>}
      {error && (
        <HelperText style={{ color: 'var(--error)' }}>
          Could not load assessment submissions
          {error instanceof Error && error.message ? `: ${error.message}` : '.'}
        </HelperText>
      )}

      {!isLoading && hasData && (
        <Card style={{ padding: 0 }}>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Submitted</th>
                  <th>Assessment</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Top results</th>
                </tr>
              </thead>
              <tbody>
                {data?.map((submission) => (
                  <ClickableRow
                    key={submission.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedSubmission(submission)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelectedSubmission(submission);
                      }
                    }}
                  >
                    <td>
                      {submission.created_at
                        ? new Date(submission.created_at).toLocaleString()
                        : '—'}
                    </td>
                    <td>{getAssessmentTypeLabel(submission.assessment_type)}</td>
                    <td>{submission.respondent_name}</td>
                    <td>{submission.respondent_email}</td>
                    <td>{formatTopResults(submission)}</td>
                  </ClickableRow>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </Card>
      )}

      {!isLoading && !hasData && !error && (
        <HelperText>No assessment submissions yet.</HelperText>
      )}

      {selectedSubmission && (
        <ModalOverlay onClick={() => setSelectedSubmission(null)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Assessment Submission</ModalTitle>
              <ModalCloseButton
                type="button"
                aria-label="Close assessment submission details"
                onClick={() => setSelectedSubmission(null)}
              >
                ×
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <DetailGroup>
                <DetailLabel>Assessment</DetailLabel>
                <DetailValue>
                  {getAssessmentTypeLabel(selectedSubmission.assessment_type)}
                </DetailValue>
              </DetailGroup>
              <DetailGroup>
                <DetailLabel>Name</DetailLabel>
                <DetailValue>{selectedSubmission.respondent_name}</DetailValue>
              </DetailGroup>
              <DetailGroup>
                <DetailLabel>Email</DetailLabel>
                <DetailValue>{selectedSubmission.respondent_email}</DetailValue>
              </DetailGroup>
              <DetailGroup>
                <DetailLabel>Phone</DetailLabel>
                <DetailValue>{selectedSubmission.respondent_phone ?? '—'}</DetailValue>
              </DetailGroup>
              <DetailGroup>
                <DetailLabel>Submitted</DetailLabel>
                <DetailValue>
                  {selectedSubmission.created_at
                    ? new Date(selectedSubmission.created_at).toLocaleString()
                    : '—'}
                </DetailValue>
              </DetailGroup>
              <DetailGroup>
                <DetailLabel>Top results</DetailLabel>
                <ResultsList>
                  {(selectedSubmission.results?.topResults ?? []).map((result) => (
                    <li key={result.categoryId}>
                      <strong>{result.label}</strong> — score {result.score}
                      {result.description ? ` — ${result.description}` : ''}
                    </li>
                  ))}
                </ResultsList>
              </DetailGroup>
              <DetailGroup>
                <DetailLabel>All rankings</DetailLabel>
                <ResultsList>
                  {(selectedSubmission.results?.rankings ?? []).map((result) => (
                    <li key={result.categoryId}>
                      {result.label}: {result.score}
                    </li>
                  ))}
                </ResultsList>
              </DetailGroup>
              <DetailGroup>
                <DetailLabel>Answers</DetailLabel>
                <AnswerList>
                  {getFormattedAnswers(selectedSubmission).map((entry) => (
                    <AnswerItem key={`${entry.questionNumber}-${entry.prompt}`}>
                      <AnswerMeta>Question {entry.questionNumber}</AnswerMeta>
                      <AnswerPrompt>{entry.prompt}</AnswerPrompt>
                      <AnswerValue>{entry.answerLabel}</AnswerValue>
                    </AnswerItem>
                  ))}
                </AnswerList>
              </DetailGroup>
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}
    </Page>
  );
};
